import { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Car, User, ArrowRight, CheckCircle2 } from 'lucide-react';

// ─── Data — one persona at a time, not a wall of boxes ──────────────────────────

const PERSONAS = {
  driver: {
    accent: 'teal',
    icon: Car,
    badgeLabel: 'For Drivers',
    title: 'TotoWala Driver',
    tagline: 'Keep maximum of every fare. Instant payouts, zero platform cuts.',
    stats: [
      { v: '₹0', l: 'Commission' },
      { v: 'Max', l: 'Fare kept' },
      { v: '1500+', l: 'Drivers' },
    ],
    benefits: ['Low commission on every ride', 'Real-time ride requests', 'Instant daily settlements'],
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.elt.totowalafordrivers',
    ctaLabel: 'Download Driver App',
  },
  rider: {
    accent: 'primary',
    icon: User,
    badgeLabel: 'For Riders',
    title: 'TotoWala Rider',
    tagline: 'Zero convenience fees. Consistent fares, no surprises.',
    stats: [
      { v: '₹0', l: 'Conv. fee' },
      { v: '2K+', l: 'Riders' },
      { v: '500+', l: 'Rides' },
    ],
    benefits: ['No convenience or booking fee', 'Transparent fare estimates', 'Fastest pickups in the city'],
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.elt.totowala',
    ctaLabel: 'Download Rider App',
  },
};

const COLOR_CLASSES = {
  teal:    { text: 'text-teal-dark dark:text-teal', solid: 'bg-teal-dark', ring: 'ring-teal-dark/15 dark:ring-teal/20' },
  primary: { text: 'text-primary',                   solid: 'bg-primary',  ring: 'ring-primary/15' },
};

const PlayStoreIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
    <path d="M3.18 23.76c.3.17.64.24.99.2l13.7-11.96-2.85-2.85L3.18 23.76zM.54 1.18C.2 1.56 0 2.13 0 2.87v18.26c0 .74.2 1.31.54 1.69l.09.08 10.23-10.23v-.24L.63 1.1l-.09.08zM20.66 10.53l-2.91-1.66-3.19 3.19 3.19 3.19 2.93-1.67c.84-.48.84-1.57-.02-2.05zM4.17.24l13.58 7.74-2.85 2.85L4.17.24z" />
  </svg>
);

// ─── Main Component ─────────────────────────────────────────────────────────────

const FeaturesSection = () => {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, margin: '-60px' });
  const [tab, setTab] = useState('driver');
  const p = PERSONAS[tab];
  const c = COLOR_CLASSES[p.accent];
  const Icon = p.icon;

  return (
    <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
      <div className="max-w-[1200px] mx-auto">

        <div ref={headerRef} className="mb-8 max-w-2xl">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 bg-teal-tint dark:bg-teal/10 border border-teal/25 text-teal-dark dark:text-teal"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-teal" />
            Why TotoWala
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45, delay: 0.06 }}
            className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-ink dark:text-dark-ink mb-3"
          >
            Drivers earn <span className="text-teal-dark dark:text-teal">more.</span>{' '}
            Riders pay <span className="text-primary">less.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.12 }}
            className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed"
          >
            No commission cuts. No hidden fees. No surge.
          </motion.p>
        </div>

        {/* Segmented toggle — same pattern a pricing page uses for Monthly/Annual */}
        <div className="inline-flex p-1 rounded-full bg-white dark:bg-dark-surface border border-line dark:border-dark-line mb-6">
          {Object.entries(PERSONAS).map(([key, persona]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`relative px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === key ? 'text-white' : 'text-ink-soft dark:text-dark-ink-soft'
              }`}
            >
              {tab === key && (
                <motion.span
                  layoutId="persona-tab-bg"
                  className={`absolute inset-0 rounded-full ${COLOR_CLASSES[persona.accent].solid}`}
                  transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                />
              )}
              <span className="relative">{persona.badgeLabel}</span>
            </button>
          ))}
        </div>

        {/* Single panel — crossfades between persona, half the height of two stacked cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line overflow-hidden"
          >
            <div className="grid md:grid-cols-[1.2fr_1fr]">

              {/* Left: identity + benefits + CTA */}
              <div className="p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-11 h-11 rounded-control flex items-center justify-center shrink-0 ${c.solid} shadow-sm`}>
                    <Icon size={20} className="text-white" strokeWidth={1.8} aria-hidden="true" />
                  </div>
                  <div>
                    <span className={`block text-[10px] font-bold uppercase tracking-wider ${c.text}`}>{p.badgeLabel}</span>
                    <h3 className="text-lg font-bold text-ink dark:text-dark-ink leading-tight">{p.title}</h3>
                  </div>
                  <div className="ml-auto hidden sm:flex items-center gap-1 text-xs font-bold text-ink-soft dark:text-dark-ink-soft">
                    4.8 <span className="text-accent">★</span>
                  </div>
                </div>

                <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed mb-5">{p.tagline}</p>

                <ul className="space-y-2 mb-6">
                  {p.benefits.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm font-medium text-ink dark:text-dark-ink">
                      <CheckCircle2 size={15} strokeWidth={2.2} className={`${c.text} shrink-0`} aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>

                <a
                  href={p.playStoreUrl}
                  target="_blank" rel="noopener noreferrer"
                  className={`inline-flex items-center justify-between gap-3 w-full sm:w-auto px-5 py-3 rounded-control font-semibold text-sm text-white transition-opacity hover:opacity-90 ${c.solid}`}
                >
                  <span className="flex items-center gap-2.5">
                    <PlayStoreIcon />
                    {p.ctaLabel}
                  </span>
                  <ArrowRight size={16} strokeWidth={2.5} aria-hidden="true" />
                </a>
              </div>

              {/* Right: stat row, same plain-number treatment as the hero — no boxed pills */}
              <div className="flex items-center justify-around gap-4 px-6 py-6 md:py-0 border-t md:border-t-0 md:border-l border-line dark:border-dark-line bg-surface dark:bg-white/[0.03]">
                {p.stats.map((s) => (
                  <div key={s.l} className="text-center">
                    <p className={`text-2xl lg:text-3xl font-black leading-none mb-1.5 ${c.text}`}>{s.v}</p>
                    <p className="text-xs text-ink-soft">{s.l}</p>
                  </div>
                ))}
              </div>

            </div>
          </motion.div>
        </AnimatePresence>

      </div>
    </section>
  );
};

export default FeaturesSection;
